import { useCallback, useEffect, useRef } from 'react';

type Options = {
  isLoadingMore: boolean;
  runLoadMore: () => void;
  isNearEnd: () => boolean;
};

export function useEndReachedLoadMore({
  isLoadingMore,
  runLoadMore,
  isNearEnd,
}: Options) {
  const isNearEndRef = useRef(isNearEnd);
  const runLoadMoreRef = useRef(runLoadMore);
  isNearEndRef.current = isNearEnd;
  runLoadMoreRef.current = runLoadMore;

  const frameRef = useRef<number | null>(null);
  const check = useCallback(() => {
    if (frameRef.current !== null) return;
    frameRef.current = requestAnimationFrame(() => {
      frameRef.current = null;
      if (isNearEndRef.current()) void runLoadMoreRef.current();
    });
  }, []);

  useEffect(
    () => () => {
      if (frameRef.current !== null) {
        cancelAnimationFrame(frameRef.current);
        frameRef.current = null;
      }
    },
    []
  );

  const wasLoadingRef = useRef(false);
  useEffect(() => {
    const justFinishedLoading = wasLoadingRef.current && !isLoadingMore;
    wasLoadingRef.current = isLoadingMore;
    if (justFinishedLoading) check();
  }, [isLoadingMore, check]);

  return { check };
}
